import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Button,
  Paper,
  alpha,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  ViewWeek as ViewWeekIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Article as ArticleIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

import { Controller } from "react-hook-form";
import useCategoryForm from "./useCategoryForm";
import { useEffect, useState } from "react";
import { CategoryEditDialog } from "@/organism/TemplateCategory";

export const CategoryForm = () => {
  const theme = useTheme();
  const [prevPageCount, setPrevPageCount] = useState(0);

  const {
    // 상태
    isLoading,
    categoryType,
    openItems,
    editDialogOpen,
    pageCategories,
    selectedCategory,

    // 폼
    createForm,

    // 핸들러
    handleCategoryTypeChange,
    handleCreateSubmit,
    handleEditOpen,
    handleEditSubmit,
    handleDelete,
    handleToggle,
    setEditDialogOpen,

    // 유틸리티
    getSectionsByPageId,
    forceRefresh, // 강제 새로고침 함수 추가
  } = useCategoryForm();

  // 페이지 카테고리 개수 변화 감지
  useEffect(() => {
    if (prevPageCount > 0 && prevPageCount !== pageCategories.length) {
      console.log(
        "페이지 카테고리 개수 변화 감지:",
        prevPageCount,
        "->",
        pageCategories.length
      );
    }
    setPrevPageCount(pageCategories.length);
  }, [pageCategories.length, prevPageCount]);

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CategoryIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant="h6" fontWeight={600}>
              카테고리 구조 관리
            </Typography>
          </Box>
          <Tooltip title="데이터 새로고침">
            <IconButton
              color="primary"
              onClick={() => {
                console.log("수동 새로고침 요청");
                forceRefresh();
              }}
              disabled={isLoading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          템플릿 내 페이지와 섹션을 구성하여 사이트 구조를 정의합니다. 페이지는
          개별 웹페이지를, 섹션은 페이지 내 컴포넌트 영역을 의미합니다.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* 카테고리 등록 폼 */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <AddIcon fontSize="small" sx={{ mr: 1 }} />새 카테고리 등록
              </Typography>

              <form onSubmit={createForm.handleSubmit(handleCreateSubmit)}>
                <Stack spacing={3}>
                  {/* 카테고리 레벨 선택 */}
                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      카테고리 유형
                    </FormLabel>
                    <Controller
                      name="categoryLevel"
                      control={createForm.control}
                      render={({ field }) => (
                        <RadioGroup
                          row
                          {...field}
                          value={categoryType}
                          onChange={(e) => {
                            const newType = parseInt(e.target.value);
                            console.log(
                              `카테고리 유형 변경: ${categoryType} -> ${newType}`
                            );
                            handleCategoryTypeChange(e);
                            field.onChange(newType); // 명시적으로 숫자로 변환하여 전달
                            console.log(
                              `폼 필드 categoryLevel 값 설정: ${newType}`
                            );
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio />}
                            label={
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <DashboardIcon
                                  fontSize="small"
                                  sx={{ mr: 0.5 }}
                                />
                                <Typography variant="body2">페이지</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio />}
                            label={
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <ViewWeekIcon
                                  fontSize="small"
                                  sx={{ mr: 0.5 }}
                                />
                                <Typography variant="body2">섹션</Typography>
                              </Box>
                            }
                            disabled={pageCategories.length === 0}
                          />
                        </RadioGroup>
                      )}
                    />
                    {categoryType === 2 && pageCategories.length === 0 && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        페이지를 먼저 등록해야 섹션을 추가할 수 있습니다.
                      </Typography>
                    )}
                  </FormControl>

                  {/* 부모 카테고리 선택 (섹션인 경우만) */}
                  {categoryType === 2 && (
                    <Controller
                      name="parentMasterCategoryId"
                      control={createForm.control}
                      rules={{ required: "페이지를 선택해주세요" }}
                      render={({ field, fieldState }) => {
                        return (
                          <FormControl fullWidth error={!!fieldState.error}>
                            <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                              상위 페이지 선택
                            </FormLabel>
                            <TextField
                              select
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                console.log("상위 페이지 선택 변경:", value);
                                field.onChange(Number(value));
                              }}
                              size="small"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            >
                              {pageCategories.length > 0 ? (
                                pageCategories.map((page) => (
                                  <MenuItem
                                    key={page.masterCategoryId}
                                    value={page.masterCategoryId}
                                  >
                                    {page.categoryName}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem disabled value="">
                                  페이지를 먼저 등록해주세요
                                </MenuItem>
                              )}
                            </TextField>
                          </FormControl>
                        );
                      }}
                    />
                  )}

                  {/* 카테고리 이름 */}
                  <Controller
                    name="categoryName"
                    control={createForm.control}
                    rules={{ required: "카테고리 이름을 입력하세요" }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth error={!!fieldState.error}>
                        <FormLabel required sx={{ fontWeight: 500, mb: 1 }}>
                          {categoryType === 1 ? "페이지 이름" : "섹션 이름"}
                        </FormLabel>
                        <TextField
                          {...field}
                          size="small"
                          placeholder="예: 메인 페이지, 헤더 섹션"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      </FormControl>
                    )}
                  />

                  {/* 카테고리 설명 - 섹션만 표시 */}
                  {categoryType === 2 && (
                    <Controller
                      name="categoryDesc"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                            섹션 설명
                          </FormLabel>
                          <TextField
                            {...field}
                            size="small"
                            multiline
                            rows={2}
                            placeholder="용도와 특징을 간략히 설명해주세요"
                          />
                        </FormControl>
                      )}
                    />
                  )}

                  {/* 노출 순서 - 섹션만 표시 */}
                  {categoryType === 2 && (
                    <Controller
                      name="dispOrder"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                            노출 순서
                          </FormLabel>
                          <TextField
                            {...field}
                            size="small"
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                          <FormHelperText>
                            낮은 숫자가 먼저 표시됩니다 (0부터 시작)
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  )}

                  {/* 앵커 ID - 섹션만 표시 */}
                  {categoryType === 2 && (
                    <Controller
                      name="anchorId"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                            앵커 ID
                          </FormLabel>
                          <TextField
                            {...field}
                            size="small"
                            placeholder="예: main-section, about-us"
                          />
                          <FormHelperText>
                            페이지 내 이동에 사용되는 고유 ID (영문, 숫자,
                            하이픈만 사용)
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  )}

                  {/* 데모 페이지 URL - 페이지만 표시 */}
                  {categoryType === 1 && (
                    <Controller
                      name="demoPageUrl"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                            데모 페이지 URL
                          </FormLabel>
                          <TextField
                            {...field}
                            size="small"
                            placeholder="https://example.com/demo"
                          />
                        </FormControl>
                      )}
                    />
                  )}

                  {/* 위치 설명 - 섹션만 표시 */}
                  {categoryType === 2 && (
                    <Controller
                      name="locationDesc"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                            위치 설명
                          </FormLabel>
                          <TextField
                            {...field}
                            size="small"
                            placeholder="예: 헤더 아래, 푸터 위"
                          />
                        </FormControl>
                      )}
                    />
                  )}

                  {/* 스크린샷 URL - 섹션만 표시 */}
                  {categoryType === 2 && (
                    <Controller
                      name="screenshotUrl"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                            스크린샷 URL
                          </FormLabel>
                          <TextField
                            {...field}
                            size="small"
                            placeholder="https://example.com/screenshot.jpg"
                          />
                        </FormControl>
                      )}
                    />
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      startIcon={<AddIcon />}
                      sx={{ py: 1 }}
                      disabled={
                        isLoading ||
                        (categoryType === 2 && pageCategories.length === 0)
                      }
                    >
                      {categoryType === 1 ? "페이지 등록" : "섹션 등록"}
                    </Button>
                    {categoryType === 2 && pageCategories.length === 0 && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 1, textAlign: "center" }}
                      >
                        페이지를 먼저 등록해야 섹션을 추가할 수 있습니다
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* 카테고리 트리 구조 */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <CategoryIcon sx={{ mr: 1 }} fontSize="small" />
                템플릿 구조
              </Typography>

              {isLoading ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">로딩 중...</Typography>
                </Box>
              ) : pageCategories.length === 0 ? (
                <Box
                  sx={{
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 3,
                  }}
                >
                  <CategoryIcon
                    sx={{ fontSize: 40, color: "text.disabled", mb: 2 }}
                  />
                  <Typography color="text.secondary" align="center">
                    아직 등록된 카테고리가 없습니다.
                    <br />
                    왼쪽 폼에서 페이지와 섹션을 추가해주세요.
                  </Typography>
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                  component="nav"
                >
                  {pageCategories.map((page) => {
                    const pageId = Number(page.masterCategoryId);
                    const sections = getSectionsByPageId(pageId);
                    const isOpen = openItems[pageId] || false;
                    const hasChildren = sections.length > 0;

                    return (
                      <Box key={pageId}>
                        <ListItem
                          sx={{
                            borderBottom: hasChildren
                              ? `1px dashed ${theme.palette.divider}`
                              : "none",
                            bgcolor: isOpen
                              ? alpha(theme.palette.primary.main, 0.04)
                              : "inherit",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <ArticleIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={500}>
                                {page.categoryName}
                              </Typography>
                            }
                            secondary={
                              page.categoryDesc ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {page.categoryDesc}
                                </Typography>
                              ) : null
                            }
                          />
                          {hasChildren && (
                            <IconButton
                              onClick={() => handleToggle(pageId)}
                              size="small"
                            >
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                          <Tooltip title="수정">
                            <IconButton
                              size="small"
                              sx={{ ml: 1 }}
                              onClick={() => handleEditOpen(page)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasChildren
                                ? "하위 카테고리가 있는 페이지는 삭제할 수 없습니다. 먼저 하위 카테고리를 삭제해주세요."
                                : "삭제"
                            }
                          >
                            <span>
                              {" "}
                              {/* Tooltip에 disabled 버튼을 바로 넣으면 작동하지 않으므로 span으로 감싸기 */}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  !hasChildren &&
                                  handleDelete(pageId, page.categoryName, true)
                                }
                                disabled={hasChildren}
                                sx={{
                                  opacity: hasChildren ? 0.5 : 1,
                                  cursor: hasChildren
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItem>

                        {sections.length > 0 && (
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {sections.map((section) => {
                                const sectionId = Number(
                                  section.masterCategoryId
                                );
                                return (
                                  <ListItem
                                    key={sectionId}
                                    sx={{
                                      pl: 4,
                                      borderTop: `1px dotted ${theme.palette.divider}`,
                                      opacity: section.hasPosts ? 1 : 0.7,
                                      backgroundColor: section.hasPosts
                                        ? "inherit"
                                        : alpha(theme.palette.grey[100], 0.5),
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <ViewWeekIcon
                                        color={
                                          section.hasPosts
                                            ? "secondary"
                                            : "disabled"
                                        }
                                        fontSize="small"
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography variant="body2">
                                            {section.categoryName}
                                          </Typography>
                                        </Box>
                                      }
                                      secondary={
                                        <Box>
                                          {section.categoryDesc && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              {section.categoryDesc}
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      {section.hasPosts && (
                                        <Tooltip title="게시물 보기">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // TODO: 게시물 보기 기능 구현
                                            }}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title="수정">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleEditOpen(section)
                                          }
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="삭제">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleDelete(
                                              sectionId,
                                              section.categoryName,
                                              false
                                            )
                                          }
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 카테고리 수정 다이얼로그 */}
      <CategoryEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        selectedCategory={selectedCategory}
        isLoading={isLoading}
        onSubmit={handleEditSubmit}
      />
    </Box>
  );
};
